<style lang="scss">
  @use "../../../../assets/css/global.scss" as *;
  @use "../../../../assets/css/_colors.scss" as *;
  @mixin text-style(
    $font-size,
    $font-weight,
    $line-height,
    $text-transform: none
  ) {
    font-family: "Lato", sans-serif;
    font-size: $font-size;
    font-weight: $font-weight;
    line-height: $line-height;
    text-transform: $text-transform;
  }

  h1 {
    @include text-style(clamp(1.3rem, 0.8vw, 1.75rem), bold, 120%);
    letter-spacing: 0;
  }

  @mixin statCardColors($firstColor, $lastColor) {
    .statValue {
      background: -webkit-linear-gradient(-35deg, $firstColor, $lastColor);
    }

    &:hover {
      border-color: $firstColor;
      box-shadow: $subtle-shadow;
    }
  }

  .statCard {
    align-items: center;
    background-color: white;
    border: 2px solid $light-gray;
    border-radius: 0.625rem;
    cursor: pointer;
    display: flex;
    padding: 1rem;
    line-height: 1;
    position: relative;
    user-select: none;

    .statDetails {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;

      .statHead {
        // align-items: center;
        align-items: end;
        display: flex;
        font-size: 1rem;
        justify-content: space-between;

        .statTitle {
          color: $text_black;
          font-weight: bold;
          text-transform: uppercase;
        }

        .infoIcon {
          color: $text-gray;

          &:hover {
            color: $blue;
          }
        }
      }

      .statValue {
        width: fit-content;
        background-clip: text;
        display: inline;
        -webkit-text-fill-color: transparent;
      }

      .statChange {
        align-items: center;
        display: flex;

        .icon {
          font-size: 90%;
          margin-right: 0.3rem;

          &.fa-caret-up {
            margin-top: 3px;
          }
        }

        span {
          color: $text_gray;
        }

        &.positive {
          color: $green;
        }

        &.neutral {
          color: #0091d9;
        }

        &.negative {
          color: $red;
        }
      }
    }

    &-messaged {
      @include statCardColors($red, #de5030);
    }

    &-calls {
      @include statCardColors(#a462e7, #6e0ea9);
    }

    &-replies {
      @include statCardColors(#20c6b7, #087c76);
    }

    &-opens {
      @include statCardColors(#0ea0d2, #0e82a9);
    }

    &-interested {
      @include statCardColors(#f234b2, #ca0f8b);
    }

    &-meetings {
      @include statCardColors(#615cf3, #3e39c4);
    }
  }

  #learnMoreHyperlink {
    color: $blue;
    &:hover {
      color: $dark-blue;
    }
  }
</style>

<script>
  import { getContext } from "svelte";
  import Tooltip from "../../components/legacy/Tooltip.svelte";
  import DashboardInfoIcon from "./DashboardInfoIcon.svelte";
  import { statCardTooltipMessages } from "./utils/statCard";
  import {
    selectedStatCardUserGroupFilter,
    selectedStatCardUserIds,
    statCardData,
  } from "./utils/stores";
  import { selectedStatCardTimeOption } from "./utils/timePeriods";
  import { showCampaignTypes, users } from "./utils/usersAndGroups";
  import { formatDatum } from "./utils/utils";

  export let key;
  export let stat;

  let { value, change, title, type, stage } = stat;
  $: ({ value, change, title, type, stage } = stat);

  let formattedStartDate;
  let formattedEndDate;

  let isTooltipSelected = false;

  const campaignTypeStore = getContext("selectedCampaignType");

  const campaignText = () => {
    if (showCampaignTypes) {
      const campaignType =
        $campaignTypeStore.value === "allCampaigns"
          ? "_all"
          : $campaignTypeStore.value;
      return `&campaignType=${campaignType}`;
    }
    return "";
  };

  const selectedSourcers = () =>
    $selectedStatCardUserIds.length
      ? `&sourcers=${$selectedStatCardUserIds.join("|")}`
      : `&sourcers=${users.map((u) => u.value).join("|")}`;

  const hyperlink = () => {
    if (!$selectedStatCardUserIds.length) return "";

    if (stage === "calls") {
      return "/todo";
    }

    return `/contacts?funnel_stage=${stage}${campaignText()}${selectedSourcers()}`;
  };

  const getOrdinalIndicator = (day) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const formatDate = (date, includeYear) => {
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const day = date.getDate();
    const monthIndex = date.getMonth();
    const year = date.getFullYear();

    return `${monthNames[monthIndex]} ${day}${getOrdinalIndicator(day)}${
      includeYear ? ` ${year}` : ""
    }`;
  };

  const formatSelectedDates = (dates) => {
    const { startDate, endDate } = dates;
    if (!startDate || !endDate) return ["", ""];

    const sameYear = startDate.split("-")[0] === endDate.split("-")[0];
    formattedStartDate = formatDate(new Date(startDate), !sameYear);
    formattedEndDate = formatDate(new Date(endDate), true);
    return [formattedStartDate, formattedEndDate];
  };

  $: formattedDates = formatSelectedDates($statCardData.selectedDates);

  $: formattedDatum = formatDatum(value, type);
</script>

<a
  class="statCard statCard-{key}"
  href={isTooltipSelected ? null : hyperlink()}
  target="_blank"
  on:click={(e) => !$selectedStatCardUserIds.length && e.preventDefault()}
>
  <div class="statDetails">
    <div class="statHead">
      <span class="statTitle text-title">{title}</span>
      <Tooltip
        width={$selectedStatCardUserIds.length ? "17rem" : "9rem"}
        fontSize="14px"
        dispatchMouseEvents
        on:tooltip-enter={() => {
          isTooltipSelected = true;
        }}
        on:tooltip-leave={() => {
          isTooltipSelected = false;
        }}
      >
        <i slot="target"
          ><DashboardInfoIcon bind:highlight={isTooltipSelected} /></i
        >
        <span slot="content">
          {#if $selectedStatCardUserIds.length}
            {#if value === null && type === "%"}
              No data available between <b>{formattedDates[0]}</b> and
              <b>{formattedDates[1]}</b>
            {:else}
              {#if $selectedStatCardTimeOption.option.value === "daily"}
                Today,
              {:else}
                Between <b>{formattedDates[0]}</b> and
                <b>{formattedDates[1]}</b>,
              {/if}
              <b>{formattedDatum}</b>
              {statCardTooltipMessages[key]}
              <a
                id="learnMoreHyperlink"
                href="https://help.sourcewhale.app/en/articles/9947266-dashboard-2-0"
                target="_blank"
                class="no-clamp">Learn more</a
              >
            {/if}
          {:else}
            No {$selectedStatCardUserGroupFilter.value || "users"} selected
          {/if}
        </span>
      </Tooltip>
    </div>
    <h1 class="statValue">
      {formattedDatum}
    </h1>
    <span
      class="statChange text-body-3-bold"
      class:positive={change > 0}
      class:neutral={!change}
      class:negative={change < 0}
    >
      {#if !Object.keys(stat).includes("change")}
        {#if $selectedStatCardUserIds.length && $selectedStatCardTimeOption.option.value !== "allTime"}
          <span>no comparison available</span>
        {/if}
      {:else}
        {#if change >= 0}
          <i class="icon fa-solid fa-caret-up"></i>
          {change}{#if type === "%"}%{/if}
        {:else}
          <i class="icon fa-solid fa-caret-down"></i>
          {change}{#if type === "%"}%{/if}
        {/if}
        <span style="margin-left: 0.3rem;"> from the previous period</span>
      {/if}
    </span>
  </div>
</a>
